import {
  Box,
  Chip,
  Drawer,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { ReactNode } from 'react';
import type {
  BelievedPosition,
  PathBelief,
  PlatformBelief,
} from '../types/entry.types';
import { isUnhedged } from '../live/unhedged';

interface EnginePositionDrawerProps {
  open: boolean;
  onClose: () => void;
  /** The live engine's believed position for the rule, or `null` when the
   *  engine hasn't emitted a live envelope yet (sim-only pod / pre-fill). */
  believedPosition: BelievedPosition | null;
  /** Pair name shown in the header so the operator knows which rule. */
  pairName?: string;
}

const fmt =(n: number | null | undefined): string =>
  n === null || n === undefined || Number.isNaN(n)
    ? '—'
    : Number.isInteger(n)
      ? String(n)
      : n.toFixed(2);

const fmtPrice = (n: number | null | undefined): string =>
  n === null || n === undefined ? '—' : `${(n * 100).toFixed(1)}¢`;

/**
 * Right-anchored drawer showing the LIVE engine's in-memory BELIEVED position
 * for a rule — what it thinks we hold per (path, platform) right now, ahead of
 * the persisted Mongo state and the venue dashboards (both of which lag). The
 * header shows the signed cross-path net per platform and the hedge imbalance;
 * each active path then breaks down into a Kalshi / Polymarket two-column
 * table. Opened from the inventory icon on each opening / unwinding row; it
 * reads `believedPosition` off the same live state the row already holds, so it
 * holds no WebSocket subscription of its own.
 */
export const EnginePositionDrawer = ({
  open,
  onClose,
  believedPosition,
  pairName,
}: EnginePositionDrawerProps) => {
  const bp = believedPosition;
  const imbalanced = isUnhedged(bp);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      keepMounted={false}
      PaperProps={{
        sx: {
          width: { xs: '100vw', md: '46vw' },
          maxWidth: '680px',
          bgcolor: '#1a1a2e',
          color: 'white',
        },
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.1)' }}
        >
          <Box>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
              Engine position
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
              {pairName ?? '—'} · the engine's live in-memory belief
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'white' }} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>

        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {!bp ? (
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255,255,255,0.6)', mt: 2 }}
            >
              No live engine state yet. The believed position appears once the
              live engine emits for this rule (after the first eval / fill).
            </Typography>
          ) : (
            <>
              {/* Net-exposure header */}
              <NetExposureHeader bp={bp} imbalanced={imbalanced} />

              {/* One section per active path */}
              {bp.paths.map((path) => (
                <PathSection
                  key={path.path}
                  path={path}
                  direction={bp.direction}
                />
              ))}
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

const NetExposureHeader = ({
  bp,
  imbalanced,
}: {
  bp: BelievedPosition;
  imbalanced: boolean;
}) => (
  <Box
    sx={{
      border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: 1,
      p: 1.5,
      mb: 2,
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
      <Typography variant="subtitle2">Net exposure</Typography>
      <Chip
        size="small"
        label={bp.direction === 'buy' ? 'OPENER' : 'UNWINDER'}
        sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white' }}
      />
    </Stack>
    <Stack direction="row" spacing={3}>
      <Metric label="Kalshi net" value={fmt(bp.kalshiNet)} />
      <Metric label="Polymarket net" value={fmt(bp.polyNet)} />
      <Tooltip title="|kalshiNet − polyNet| — 0 means the position is delta-neutral (every Kalshi-NO matched by a Poly-YES). A non-zero value flags an unhedged or oversold leg.">
        <Box>
          <Metric
            label="Leg imbalance"
            value={fmt(bp.legImbalance)}
            color={imbalanced ? '#ff6b6b' : '#51cf66'}
          />
        </Box>
      </Tooltip>
    </Stack>
  </Box>
);

const Metric = ({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) => (
  <Box>
    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
      {label}
    </Typography>
    <Typography variant="h6" sx={{ color: color ?? 'white', lineHeight: 1.2 }}>
      {value}
    </Typography>
  </Box>
);

const PathSection = ({
  path,
  direction,
}: {
  path: PathBelief;
  direction: 'buy' | 'sell';
}) => {
  // Path A = Poly-YES / Kalshi-NO, B = Poly-NO / Kalshi-YES.
  const label = path.path === 'a' ? 'Path A' : 'Path B';
  const sides = path.path === 'a' ? 'Poly-YES · Kalshi-NO' : 'Poly-NO · Kalshi-YES';
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {label}{' '}
        <Typography
          component="span"
          variant="caption"
          sx={{ color: 'rgba(255,255,255,0.5)' }}
        >
          {sides}
        </Typography>
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <Cell head>Component</Cell>
            <Cell head align="right">
              Kalshi
            </Cell>
            <Cell head align="right">
              Polymarket
            </Cell>
          </TableRow>
        </TableHead>
        <TableBody>
          <LegRow
            label="Net believed"
            k={path.kalshi}
            p={path.polymarket}
            get={(b) => fmt(b.net)}
            highlightNeg
            bold
          />
          <MakerRow k={path.kalshi} p={path.polymarket} />
          <LegRow
            label="Side"
            k={path.kalshi}
            p={path.polymarket}
            get={(b) => b.side.toUpperCase()}
          />
          {direction === 'buy' ? (
            <>
              <LegRow
                label="Starting"
                k={path.kalshi}
                p={path.polymarket}
                get={(b) => fmt(b.starting)}
              />
              <LegRow
                label="Total bought"
                k={path.kalshi}
                p={path.polymarket}
                get={(b) => fmt(b.totalBought)}
              />
              <LegRow
                label="Cycle bought"
                k={path.kalshi}
                p={path.polymarket}
                get={(b) => fmt(b.contractsBoughtCycle)}
              />
            </>
          ) : (
            <LegRow
              label="Available to sell"
              k={path.kalshi}
              p={path.polymarket}
              get={(b) => fmt(b.available)}
              highlightNeg
            />
          )}
          <LegRow
            label="Maker filled (cycle)"
            k={path.kalshi}
            p={path.polymarket}
            get={(b) => fmt(b.makerFilledQty)}
          />
          <LegRow
            label="Counter filled (cycle)"
            k={path.kalshi}
            p={path.polymarket}
            get={(b) => fmt(b.counterFilledQty)}
          />
          <LegRow
            label="Unhedged partial"
            k={path.kalshi}
            p={path.polymarket}
            get={(b) => fmt(b.unhedgedPartial)}
          />
          <LegRow
            label="Superseded makers"
            k={path.kalshi}
            p={path.polymarket}
            get={(b) => fmt(b.supersededTotal)}
          />
          <LegRow
            label="Volume / cap"
            k={path.kalshi}
            p={path.polymarket}
            get={(b) =>
              `${fmt(b.volumeExecuted)} / ${b.maxVolume > 0 ? fmt(b.maxVolume) : '∞'}`
            }
          />
        </TableBody>
      </Table>
    </Box>
  );
};

const Cell = ({
  children,
  head,
  align,
}: {
  children: ReactNode;
  head?: boolean;
  align?: 'right' | 'left';
}) => (
  <TableCell
    align={align ?? 'left'}
    sx={{
      color: head ? 'rgba(255,255,255,0.5)' : 'white',
      borderBottom: '1px solid rgba(255,255,255,0.08)',
      fontSize: 12,
      py: 0.5,
    }}
  >
    {children}
  </TableCell>
);

const LegRow = ({
  label,
  k,
  p,
  get,
  highlightNeg,
  bold,
}: {
  label: string;
  k: PlatformBelief;
  p: PlatformBelief;
  get: (b: PlatformBelief) => string;
  highlightNeg?: boolean;
  bold?: boolean;
}) => {
  const cellSx = (b: PlatformBelief) => ({
    fontWeight: bold ? 700 : 400,
    color: highlightNeg && b.net < 0 ? '#ff6b6b' : 'white',
  });
  return (
    <TableRow>
      <Cell>{label}</Cell>
      <TableCell align="right" sx={{ ...baseNumCell, ...cellSx(k) }}>
        {get(k)}
      </TableCell>
      <TableCell align="right" sx={{ ...baseNumCell, ...cellSx(p) }}>
        {get(p)}
      </TableCell>
    </TableRow>
  );
};

/** The maker row badges which leg is currently the resting maker + its price. */
const MakerRow = ({ k, p }: { k: PlatformBelief; p: PlatformBelief }) => {
  const cell = (b: PlatformBelief) =>
    b.isMaker ? (
      <Stack
        direction="row"
        spacing={0.5}
        justifyContent="flex-end"
        alignItems="center"
      >
        <Chip
          size="small"
          label="MAKER"
          sx={{
            height: 18,
            fontSize: 10,
            bgcolor: 'rgba(34,197,94,0.3)',
            color: 'white',
          }}
        />
        <span>{fmtPrice(b.makerPrice)}</span>
      </Stack>
    ) : (
      '—'
    );
  return (
    <TableRow>
      <Cell>Maker</Cell>
      <TableCell align="right" sx={baseNumCell}>
        {cell(k)}
      </TableCell>
      <TableCell align="right" sx={baseNumCell}>
        {cell(p)}
      </TableCell>
    </TableRow>
  );
};

const baseNumCell = {
  color: 'white',
  borderBottom: '1px solid rgba(255,255,255,0.08)',
  fontSize: 12,
  py: 0.5,
  fontVariantNumeric: 'tabular-nums',
} as const;
